import { useEffect, useMemo, useState, type DragEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "../../../../components/ui";

// ===========================================================================
// Piezas de la consola operativa tipo iVMS-4200 de Nuvex Access: página a todo
// el alto, KPIs, chips de estado, árbol de sitios, panel lateral (drawer) y
// estados de carga / vacío / error. Sólo tokens semánticos (claro y oscuro).
// ===========================================================================

export type ChipTone = "success" | "warning" | "error" | "info" | "neutral" | "primary";

const CHIP_TONES = {
  success: "bg-success/15 text-success",
  warning: "bg-warning/15 text-warning",
  error: "bg-error/15 text-error",
  info: "bg-info/15 text-info",
  neutral: "bg-subtle text-content-secondary",
  primary: "bg-primary-500/15 text-primary-500",
} as const satisfies Record<ChipTone, string>;

const DOT_TONES = {
  success: "bg-success",
  warning: "bg-warning",
  error: "bg-error",
  info: "bg-info",
  neutral: "bg-content-faint",
  primary: "bg-primary-500",
} as const satisfies Record<ChipTone, string>;

// Fondo opaco para chips que van ENCIMA de una foto o video: el tono del chip es
// translúcido (bg-*/15) y sobre una imagen no se lee; con `overlay` se monta sobre
// una base clara/oscura semitransparente con desenfoque y borde.
export const CHIP_OVERLAY_BASE = "border border-black/10 bg-white/90 shadow-sm backdrop-blur-sm dark:border-white/15 dark:bg-gray-900/80";

export function StatusChip({ tone, children, pulse = false, overlay = false }: { tone: ChipTone; children: ReactNode; pulse?: boolean; overlay?: boolean }) {
  const chip = (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${CHIP_TONES[tone]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${DOT_TONES[tone]} ${pulse ? "animate-pulse" : ""}`} />
      {children}
    </span>
  );
  if (!overlay) return chip;
  return (
    <span data-chip-overlay="" className={`inline-flex rounded-full ${CHIP_OVERLAY_BASE}`}>
      {chip}
    </span>
  );
}

export function StatusDot({ tone }: { tone: ChipTone }) {
  return <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${DOT_TONES[tone]}`} />;
}

// --- Tonos por dominio -----------------------------------------------------------
export const CAMERA_TONE = { online: "success", recording: "error", offline: "neutral", maintenance: "warning" } as const satisfies Record<string, ChipTone>;
export const POINT_TONE = { online: "success", offline: "neutral", maintenance: "warning", alarm: "error" } as const satisfies Record<string, ChipTone>;
export const PASS_TONE = { active: "success", scheduled: "info", used: "primary", expired: "neutral", revoked: "error" } as const satisfies Record<string, ChipTone>;
export const SEVERITY_TONE = { critical: "error", high: "warning", medium: "info", low: "neutral" } as const satisfies Record<string, ChipTone>;
export const ALERT_STATUS_TONE = { open: "error", acknowledged: "warning", resolved: "success" } as const satisfies Record<string, ChipTone>;
export const RESULT_TONE = { granted: "success", denied: "error" } as const satisfies Record<string, ChipTone>;

export const toneOf = <M extends Record<string, ChipTone>>(map: M, key: string | undefined | null): ChipTone =>
  (key && (map as Record<string, ChipTone>)[key]) || "neutral";

// --- Iconos ----------------------------------------------------------------------
export const POINT_ICON: Record<string, string> = {
  door: "ri-door-closed-line",
  turnstile: "ri-git-commit-line",
  barrier: "ri-parking-box-line",
  gate: "ri-door-open-line",
  elevator: "ri-building-line",
};
export const METHOD_ICON: Record<string, string> = {
  card: "ri-bank-card-line",
  face: "ri-user-smile-line",
  qr: "ri-qr-code-line",
  plate: "ri-car-line",
  pin: "ri-lock-password-line",
  fingerprint: "ri-fingerprint-line",
  manual: "ri-hand-coin-line",
};
export const SITE_ICON: Record<string, string> = {
  yard: "ri-truck-line",
  warehouse: "ri-store-3-line",
  office: "ri-building-2-line",
  facility: "ri-map-pin-line",
};
/** Cámara de vigilancia. Remix Icon 4.3 (el que carga index.html) no trae `ri-cctv-*`. */
export const CAMERA_ICON = "ri-webcam-line";

// --- Fechas ------------------------------------------------------------------------
export function relativeTime(iso: string | null | undefined, locale: string, nowMs = Date.now()): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  const diff = (ms - nowMs) / 1000;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  return rtf.format(Math.round(diff / 86400), "day");
}

export function formatDateTime(iso: string | null | undefined, locale: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
}

/** Hora actual que se refresca sola (para fechas relativas en pantallas en vivo). */
export function useTicker(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

// --- Página ---------------------------------------------------------------------
export function ConsolePage({
  icon,
  title,
  subtitle,
  actions,
  children,
  fill = false,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  children: ReactNode;
  /** Ocupa todo el alto sin scroll de página (el mosaico de cámaras). */
  fill?: boolean;
}) {
  return (
    <div className={`flex h-full w-full min-w-0 flex-col bg-app ${fill ? "overflow-hidden" : "overflow-y-auto"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-surface px-4 py-3 sm:px-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-500/15 text-primary-500">
            <i className={`${icon} text-lg`} />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-base font-bold text-content">{title}</h1>
            {subtitle && <p className="truncate text-xs text-content-secondary">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {/* Sin `fill`, la página hace scroll completo: el cuerpo NO lleva min-h-0, porque
          con él los paneles (min-h-0 + overflow-hidden) se encogían a una franja vacía
          cuando el contenido no cabía en el alto de la ventana (Cortes de caja). */}
      <div className={`flex flex-1 flex-col ${fill ? "min-h-0" : "gap-4 p-4 sm:p-5"}`}>{children}</div>
    </div>
  );
}

export function KpiRow({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">{children}</div>;
}

export function KpiTile({
  icon,
  label,
  value,
  hint,
  tone = "primary",
}: {
  icon: string;
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: ChipTone;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-line bg-card px-3 py-3">
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-lg ${CHIP_TONES[tone]}`}>
        <i className={icon} />
      </span>
      <div className="min-w-0">
        <div className="truncate text-[11px] font-medium uppercase tracking-wide text-content-muted">{label}</div>
        <div className="text-xl font-bold leading-tight text-content tabular-nums">{value}</div>
        {hint && <div className="truncate text-[11px] text-content-secondary">{hint}</div>}
      </div>
    </div>
  );
}

export function Panel({ title, icon, actions, children, className = "", bodyClassName = "p-3" }: { title?: string; icon?: string; actions?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string }) {
  return (
    <section className={`flex min-h-0 flex-col overflow-hidden rounded-xl border border-line bg-card ${className}`}>
      {(title || actions) && (
        <header className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
          <h2 className="flex min-w-0 items-center gap-2 truncate text-sm font-semibold text-content">
            {icon && <i className={`${icon} text-content-muted`} />}
            {title}
          </h2>
          {actions}
        </header>
      )}
      <div className={`min-h-0 flex-1 ${bodyClassName}`}>{children}</div>
    </section>
  );
}

// --- Estados ------------------------------------------------------------------------
export function LoadState({ loading, error, empty, emptyText, onRetry, children }: { loading: boolean; error: unknown; empty: boolean; emptyText?: string; onRetry?: () => void; children: ReactNode }) {
  const { t } = useTranslation("accessConsole");
  if (loading)
    return (
      <div className="flex min-h-[160px] items-center justify-center gap-2 text-sm text-content-secondary">
        <Spinner size="sm" /> {t("common.loading")}
      </div>
    );
  if (error)
    return (
      <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 text-center text-sm text-content-secondary">
        <i className="ri-error-warning-line text-2xl text-error" />
        {t("common.error")}
        {onRetry && (
          <button type="button" onClick={onRetry} className="rounded-md border border-line px-3 py-1 text-xs text-content hover:bg-subtle">
            {t("common.retry")}
          </button>
        )}
      </div>
    );
  if (empty)
    return (
      <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 text-center text-sm text-content-secondary">
        <i className="ri-inbox-line text-2xl text-content-faint" />
        {emptyText ?? t("common.empty")}
      </div>
    );
  return <>{children}</>;
}

// --- Drawer ---------------------------------------------------------------------------
export function Drawer({ open, title, subtitle, onClose, children, footer }: { open: boolean; title: string; subtitle?: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  const { t } = useTranslation("accessConsole");
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[65] flex justify-end" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" aria-label={t("common.close")} className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="relative flex h-full w-full max-w-md flex-col border-l border-line bg-surface shadow-2xl">
        <header className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-content">{title}</h2>
            {subtitle && <p className="truncate text-xs text-content-secondary">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label={t("common.close")} className="rounded-md p-1 text-content-secondary hover:bg-subtle hover:text-content">
            <i className="ri-close-line text-xl" />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        {footer && <footer className="flex flex-wrap justify-end gap-2 border-t border-line px-4 py-3">{footer}</footer>}
      </aside>
    </div>
  );
}

export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line-soft py-2 text-sm last:border-0">
      <span className="shrink-0 text-content-secondary">{label}</span>
      <span className="min-w-0 text-right font-medium text-content">{children}</span>
    </div>
  );
}

// --- Árbol de sitios ------------------------------------------------------------------
export interface TreeItem {
  id: string;
  label: string;
  icon: string;
  tone?: ChipTone;
  meta?: string;
  draggableMime?: string;
}
export interface TreeGroup {
  id: string;
  label: string;
  icon: string;
  items: TreeItem[];
}

export function SiteTree({
  title,
  groups,
  selectedGroup,
  selectedItem,
  onSelectGroup,
  onSelectItem,
  onActivateItem,
  allLabel,
  className = "",
}: {
  title: string;
  groups: TreeGroup[];
  selectedGroup?: string | null;
  selectedItem?: string | null;
  onSelectGroup?: (id: string | null) => void;
  onSelectItem?: (id: string) => void;
  /** Doble clic o Enter sobre un elemento (p. ej. mandar la cámara al mosaico). */
  onActivateItem?: (id: string) => void;
  allLabel?: string;
  className?: string;
}) {
  const { t } = useTranslation("accessConsole");
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      groups
        .map((g) => ({ ...g, items: q ? g.items.filter((it) => it.label.toLowerCase().includes(q) || g.label.toLowerCase().includes(q)) : g.items }))
        .filter((g) => !q || g.items.length),
    [groups, q],
  );
  const total = groups.reduce((a, g) => a + g.items.length, 0);

  const onDragStart = (e: DragEvent, it: TreeItem) => {
    if (!it.draggableMime) return;
    e.dataTransfer.setData(it.draggableMime, it.id);
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <aside className={`flex min-h-0 flex-col border-line bg-surface ${className}`}>
      <div className="border-b border-line px-3 py-2">
        <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-content-muted">
          <span>{title}</span>
          <span className="tabular-nums">{total}</span>
        </div>
        <label className="flex items-center gap-2 rounded-md border border-line bg-app px-2 py-1.5">
          <i className="ri-search-line text-content-faint" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("common.search")}
            aria-label={t("common.search")}
            className="w-full bg-transparent text-sm text-content outline-none placeholder:text-content-faint"
          />
        </label>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto py-1 text-sm" data-scroll-region="tree">
        {onSelectGroup && (
          <button
            type="button"
            onClick={() => onSelectGroup(null)}
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${!selectedGroup ? "bg-primary-500/10 font-semibold text-primary-500" : "text-content hover:bg-subtle"}`}
          >
            <i className="ri-global-line" />
            <span className="flex-1 truncate">{allLabel ?? t("common.allSites")}</span>
          </button>
        )}
        {filtered.map((g) => {
          const isCollapsed = collapsed[g.id] && !q;
          return (
            <div key={g.id}>
              <div className={`flex items-center ${selectedGroup === g.id ? "bg-primary-500/10" : ""}`}>
                <button
                  type="button"
                  aria-label={isCollapsed ? t("common.expand") : t("common.collapse")}
                  onClick={() => setCollapsed((c) => ({ ...c, [g.id]: !c[g.id] }))}
                  className="px-1.5 py-1.5 text-content-muted hover:text-content"
                >
                  <i className={isCollapsed ? "ri-arrow-right-s-line" : "ri-arrow-down-s-line"} />
                </button>
                <button
                  type="button"
                  onClick={() => onSelectGroup?.(g.id)}
                  className={`flex min-w-0 flex-1 items-center gap-2 py-1.5 pr-3 text-left ${selectedGroup === g.id ? "font-semibold text-primary-500" : "font-medium text-content"}`}
                >
                  <i className={g.icon} />
                  <span className="flex-1 truncate">{g.label}</span>
                  <span className="text-[11px] tabular-nums text-content-muted">{g.items.length}</span>
                </button>
              </div>
              {!isCollapsed &&
                g.items.map((it) => (
                  <div
                    key={it.id}
                    role="button"
                    tabIndex={0}
                    draggable={Boolean(it.draggableMime)}
                    onDragStart={(e) => onDragStart(e, it)}
                    onClick={() => onSelectItem?.(it.id)}
                    onDoubleClick={() => onActivateItem?.(it.id)}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      if (onActivateItem) onActivateItem(it.id);
                      else onSelectItem?.(it.id);
                    }}
                    title={it.label}
                    className={`flex cursor-pointer items-center gap-2 py-1.5 pl-9 pr-3 ${
                      selectedItem === it.id ? "bg-primary-500/15 text-primary-500" : "text-content-secondary hover:bg-subtle hover:text-content"
                    } ${it.draggableMime ? "active:cursor-grabbing" : ""}`}
                  >
                    <i className={`${it.icon} text-[13px]`} />
                    <span className="min-w-0 flex-1 truncate">{it.label}</span>
                    {it.meta && <span className="text-[10px] text-content-faint">{it.meta}</span>}
                    {it.tone && <StatusDot tone={it.tone} />}
                  </div>
                ))}
            </div>
          );
        })}
        {!filtered.length && <p className="px-3 py-4 text-center text-xs text-content-muted">{t("common.empty")}</p>}
      </div>
    </aside>
  );
}

// --- Barra de herramientas ----------------------------------------------------------
export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const { t } = useTranslation("accessConsole");
  return (
    <label className="flex min-w-[180px] flex-1 items-center gap-2 rounded-lg border border-line bg-card px-3 py-1.5 sm:max-w-xs">
      <i className="ri-search-line text-content-faint" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? t("common.search")}
        aria-label={placeholder ?? t("common.search")}
        className="w-full bg-transparent text-sm text-content outline-none placeholder:text-content-faint"
      />
    </label>
  );
}

export function FilterSelect({ value, onChange, options, label }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; label: string }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className="rounded-lg border border-line bg-card px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary-500"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function SegmentedToggle<T extends string | number>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; title?: string }[]; label: string }) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex overflow-hidden rounded-lg border border-line bg-card">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          title={o.title}
          onClick={() => onChange(o.value)}
          className={`px-2.5 py-1.5 text-sm ${value === o.value ? "bg-primary-500 text-on-primary" : "text-content-secondary hover:bg-subtle"}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function ToolButton({ icon, children, onClick, variant = "secondary", disabled, title }: { icon?: string; children?: ReactNode; onClick?: () => void; variant?: "primary" | "secondary" | "danger"; disabled?: boolean; title?: string }) {
  const cls =
    variant === "primary"
      ? "bg-primary-500 text-on-primary hover:bg-primary-400"
      : variant === "danger"
        ? "bg-error/10 text-error hover:bg-error/20"
        : "border border-line bg-card text-content hover:bg-subtle";
  return (
    <button type="button" onClick={onClick} disabled={disabled} title={title} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-50 ${cls}`}>
      {icon && <i className={icon} />}
      {children}
    </button>
  );
}

/** Iniciales para avatares (nunca fotos de personas ficticias). */
export function Initials({ name, size = "md" }: { name: string; size?: "sm" | "md" | "lg" }) {
  const parts = name.replace(/^(Ing|Lic|Dr|Dra)\.\s*/i, "").split(/\s+/).filter(Boolean);
  const ini = ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
  const sz = size === "lg" ? "h-14 w-14 text-lg" : size === "sm" ? "h-7 w-7 text-[11px]" : "h-9 w-9 text-xs";
  return <span className={`flex shrink-0 items-center justify-center rounded-full bg-primary-500/15 font-bold text-primary-500 ${sz}`}>{ini || "?"}</span>;
}
